import { Autocomplete as MuiAutocomplete, AutocompleteProps as MuiAutocompleteProps, TextField as MuiTextField } from '@mui/material';
import { ReactNode } from 'react';
import {
    FieldPath, FieldValues, UseControllerProps, useController
} from 'react-hook-form';

import { addDefaultRequiredRule } from '.';


type AutocompleteProps<
    TFieldValues extends FieldValues,
    TName extends FieldPath<TFieldValues>
> = UseControllerProps<TFieldValues, TName>
    & Omit<MuiAutocompleteProps<string, boolean, boolean, boolean>, 'renderInput'>
    & {
        label?: ReactNode;
        required?: boolean;
        helperText?: string;
        autoFocus?: boolean;
    };

/**
 * Renders a react-hook-form controlled mui Autocomplete component.
 *
 * @example
 * ```ts
 * import { FormProvider, useForm } from 'react-hook-form';
 * import { Autocomplete } from '@mui/material';
 *
 * const formMethods = useForm();
 *
 * <FormProvider {...formMethods}>
 *     <Autocomplete name="name" />
 * </FormProvider>
 * ```
 *
 * @param props - The props for the Controller and the underlying Autocomplete
 * @constructor
 */
export function Autocomplete<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
    autoFocus, rules: rulesProp, name, defaultValue, freeSolo, onInputChange, label, required, helperText, ...otherProps
}: AutocompleteProps<TFieldValues, TName>) {
    const {
        field: { onChange, ...otherFieldProps },
        fieldState: { error }
    } = useController({
        name,
        rules: addDefaultRequiredRule(rulesProp, required, label),
        defaultValue
    });

    return (
        <MuiAutocomplete
            {...otherProps}
            onInputChange={(event, value, reason) => {
                freeSolo && onChange(value);
                onInputChange?.(event, value, reason);
            }}
            {...otherFieldProps}
            onChange={(event, value) => {
                onChange(value);
            }}
            freeSolo={freeSolo}
            renderInput={params => (
                <MuiTextField
                    {...params}
                    autoFocus={autoFocus}
                    label={label}
                    error={!!error?.message}
                    helperText={error ? error.message : helperText}
                    required={required}
                />
            )}
        />
    );
}
